import React, { useEffect, useState, useRef } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell
} from 'recharts';
import html2canvas from 'html2canvas';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, attendanceAPI, taskAPI, leaveAPI } from '../services/api';
import './Dashboard.css';

/* ── Helpers ── */
const fmt = (t) => t ? new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const today = () => new Date().toISOString().split('T')[0];

/* ══════════════════════════════════════════════
   EMPLOYEE PERSONAL DASHBOARD
══════════════════════════════════════════════ */
const EmployeeDashboard = ({ currentUser }) => {
  const [todayAtt, setTodayAtt]     = useState(null);
  const [attSummary, setAttSummary] = useState(null);
  const [attRecent, setAttRecent]   = useState([]);
  const [tasks, setTasks]           = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [todayRes, myAttRes, tasksRes] = await Promise.allSettled([
          attendanceAPI.getToday(),
          attendanceAPI.getMy({ from: monthStart(), to: today(), limit: 30 }),
          taskAPI.getAll(),
        ]);

        if (todayRes.status === 'fulfilled') setTodayAtt(todayRes.value);
        if (myAttRes.status === 'fulfilled') {
          setAttSummary(myAttRes.value?.summary || null);
          setAttRecent((myAttRes.value?.data || []).slice(0, 7));
        }
        if (tasksRes.status === 'fulfilled') {
          setTasks(tasksRes.value?.data || []);
        }

        // Leave balance — try with user _id
        if (currentUser?._id) {
          try {
            const lb = await leaveAPI.getBalance(currentUser._id);
            setLeaveBalance(lb?.data || lb || null);
          } catch {}
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px', color: '#9ca3af' }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: 32 }}></i>
        <p style={{ marginTop: 14, fontSize: 15 }}>Loading your dashboard...</p>
      </div>
    );
  }

  /* ── Derive values ── */
  const checkedIn  = todayAtt?.checkedIn  || false;
  const checkedOut = todayAtt?.checkedOut || false;
  const checkInTime = todayAtt?.data?.checkIn;
  const checkOutTime = todayAtt?.data?.checkOut;
  const todayHours  = todayAtt?.data?.workHours;

  const presentDays = attSummary?.presentDays ?? '—';
  const totalHours  = attSummary?.totalHours  ?? '—';

  const myTasks      = tasks;
  const pendingTasks = myTasks.filter(t => t.status === 'pending' || t.status === 'in-progress').length;
  const doneTasks    = myTasks.filter(t => t.status === 'completed').length;

  const statusColor = {
    present:    { bg: '#d1fae5', color: '#065f46' },
    late:       { bg: '#fef3c7', color: '#92400e' },
    'half-day': { bg: '#fed7aa', color: '#9a3412' },
    absent:     { bg: '#fee2e2', color: '#991b1b' },
    'on-leave': { bg: '#dbeafe', color: '#1e40af' },
  };
  const getBadge = (s) => statusColor[(s || '').toLowerCase()] || { bg: '#f3f4f6', color: '#374151' };

  const taskStatusColor = {
    pending:      { bg: '#fef9c3', color: '#854d0e' },
    'in-progress':{ bg: '#dbeafe', color: '#1e40af' },
    'on-hold':    { bg: '#f3e8ff', color: '#6b21a8' },
    completed:    { bg: '#d1fae5', color: '#065f46' },
    cancelled:    { bg: '#fee2e2', color: '#991b1b' },
  };
  const getTaskBadge = (s) => taskStatusColor[(s || '').toLowerCase()] || { bg: '#f3f4f6', color: '#374151' };

  /* ── Attendance chart data ── */
  const attChartData = [...attRecent].reverse().map(r => ({
    date: fmtDate(r.date),
    hours: r.workHours || 0,
  }));

  return (
    <div className="dashboard">
      {/* ── Welcome Banner ── */}
      <div className="emp-welcome-banner">
        <div className="emp-welcome-left">
          <h2>Welcome back, {currentUser?.name?.split(' ')[0] || 'there'} 👋</h2>
          <p>{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        </div>
        <div className="emp-today-attendance">
          {!checkedIn && (
            <span className="emp-att-status emp-att-status--notIn">
              <i className="fas fa-circle-xmark"></i> Aaj login nahi kiya
            </span>
          )}
          {checkedIn && !checkedOut && (
            <span className="emp-att-status emp-att-status--in">
              <i className="fas fa-circle-check"></i> Logged in at {fmt(checkInTime)}
            </span>
          )}
          {checkedIn && checkedOut && (
            <span className="emp-att-status emp-att-status--done">
              <i className="fas fa-check-double"></i> Day complete · {todayHours ? todayHours + 'h worked' : fmt(checkInTime) + ' – ' + fmt(checkOutTime)}
            </span>
          )}
        </div>
      </div>

      {/* ── Stat Cards ── */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green"><i className="fas fa-calendar-check"></i></div>
          <div className="stat-info">
            <h3>{presentDays}</h3>
            <p>Present This Month</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue"><i className="fas fa-clock"></i></div>
          <div className="stat-info">
            <h3>{totalHours !== '—' ? totalHours + 'h' : '—'}</h3>
            <p>Hours This Month</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><i className="fas fa-tasks"></i></div>
          <div className="stat-info">
            <h3>{pendingTasks}</h3>
            <p>Pending Tasks</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><i className="fas fa-check-circle"></i></div>
          <div className="stat-info">
            <h3>{doneTasks}</h3>
            <p>Tasks Completed</p>
          </div>
        </div>
      </div>

      <div className="dashboard-charts">
        {/* ── Attendance Hours Chart ── */}
        {attChartData.length > 0 && (
          <div className="chart-card full-width">
            <h3><i className="fas fa-chart-bar"></i> My Attendance This Month (Work Hours)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={attChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" tick={{ fontSize: 12 }} />
                <YAxis stroke="#6b7280" />
                <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                <Bar dataKey="hours" name="Work Hours" fill="#6366f1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* ── My Recent Tasks ── */}
        <div className="chart-card">
          <h3><i className="fas fa-tasks"></i> My Tasks</h3>
          {myTasks.length === 0 ? (
            <div className="emp-empty">No tasks assigned</div>
          ) : (
            <div className="emp-task-list">
              {myTasks.slice(0, 8).map(t => {
                const tb = getTaskBadge(t.status);
                return (
                  <div key={t._id} className="emp-task-item">
                    <div className="emp-task-title">{t.title}</div>
                    <div className="emp-task-meta">
                      <span className="emp-mini-badge" style={{ background: tb.bg, color: tb.color }}>{t.status}</span>
                      {t.dueDate && <span className="emp-due">Due {fmtDate(t.dueDate)}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── My Recent Attendance ── */}
        <div className="chart-card">
          <h3><i className="fas fa-calendar-alt"></i> My Recent Attendance</h3>
          {attRecent.length === 0 ? (
            <div className="emp-empty">No attendance records</div>
          ) : (
            <table className="emp-att-table">
              <thead>
                <tr><th>Date</th><th>In</th><th>Out</th><th>Hrs</th><th>Status</th></tr>
              </thead>
              <tbody>
                {attRecent.map(r => {
                  const b = getBadge(r.status);
                  return (
                    <tr key={r._id}>
                      <td>{fmtDate(r.date)}</td>
                      <td>{fmt(r.checkIn)}</td>
                      <td>{fmt(r.checkOut)}</td>
                      <td>{r.workHours != null ? r.workHours + 'h' : '—'}</td>
                      <td><span className="emp-mini-badge" style={{ background: b.bg, color: b.color }}>{r.status || '—'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Leave Balance ── */}
        {leaveBalance && (
          <div className="chart-card">
            <h3><i className="fas fa-umbrella-beach"></i> My Leave Balance</h3>
            <div className="emp-leave-grid">
              {Object.entries(leaveBalance).map(([key, val]) => (
                typeof val === 'number' ? (
                  <div key={key} className="emp-leave-card">
                    <div className="emp-leave-val">{val}</div>
                    <div className="emp-leave-lbl">{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</div>
                  </div>
                ) : null
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .emp-welcome-banner {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          border-radius: 16px;
          padding: 24px 28px;
          color: white;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 24px;
        }
        .emp-welcome-banner h2 { margin: 0; font-size: 22px; font-weight: 700; }
        .emp-welcome-banner p  { margin: 4px 0 0; opacity: 0.85; font-size: 14px; }
        .emp-today-attendance  { display: flex; align-items: center; }
        .emp-att-status {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          border-radius: 30px;
          font-size: 14px;
          font-weight: 600;
          white-space: nowrap;
        }
        .emp-att-status--notIn { background: rgba(239,68,68,0.25); color: #fecaca; }
        .emp-att-status--in    { background: rgba(16,185,129,0.25); color: #a7f3d0; }
        .emp-att-status--done  { background: rgba(255,255,255,0.2); color: #fff; }
        .emp-task-list { display: flex; flex-direction: column; gap: 10px; }
        .emp-task-item { display: flex; flex-direction: column; gap: 5px; padding: 10px 12px; background: #f8fafc; border-radius: 8px; border-left: 3px solid #6366f1; }
        .emp-task-title { font-size: 14px; font-weight: 500; color: #111827; }
        .emp-task-meta { display: flex; align-items: center; gap: 10px; }
        .emp-due { font-size: 12px; color: #9ca3af; }
        .emp-mini-badge { padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; text-transform: capitalize; }
        .emp-att-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .emp-att-table th { text-align: left; padding: 8px 10px; background: #f8fafc; color: #6b7280; font-size: 11px; font-weight: 700; text-transform: uppercase; border-bottom: 1px solid #e5e7eb; }
        .emp-att-table td { padding: 9px 10px; border-bottom: 1px solid #f1f5f9; color: #374151; }
        .emp-att-table tr:last-child td { border-bottom: none; }
        .emp-empty { text-align: center; color: #9ca3af; padding: 32px; font-size: 14px; }
        .emp-leave-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 12px; }
        .emp-leave-card { background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 14px 12px; text-align: center; }
        .emp-leave-val  { font-size: 24px; font-weight: 700; color: #059669; }
        .emp-leave-lbl  { font-size: 11px; color: #6b7280; margin-top: 4px; font-weight: 500; }
      `}</style>
    </div>
  );
};

/* ══════════════════════════════════════════════
   ADMIN / HR COMPANY DASHBOARD
══════════════════════════════════════════════ */
const AdminDashboard = () => {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesPeriod, setSalesPeriod] = useState('today');
  const [overallPeriod, setOverallPeriod] = useState('weekly');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const chartCardRef = useRef(null);

  useEffect(() => {
    dashboardAPI.getStats()
      .then(res => setStats(res?.data || res || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCustomDateChange = (start, end) => {
    if (start && end) {
      setLoading(true);
      dashboardAPI.getStats({ startDate: start, endDate: end })
        .then(res => {
          const customData = res?.data?.salesPerformance?.custom || res?.salesPerformance?.custom;
          if (customData) {
            setStats(prev => ({
              ...prev,
              salesPerformance: {
                ...prev.salesPerformance,
                custom: customData
              }
            }));
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  };

  // Fallback static charts when API data not available
  const attendanceData = stats?.attendanceTrend || [];

  const getSalesPerformanceData = () => {
    const periodData = stats?.salesPerformance?.[salesPeriod] || {};
    return [
      { name: 'Calls', count: periodData.calls || 0, color: '#3b82f6' },
      { name: 'Converted', count: periodData.converted || 0, color: '#10b981' },
      { name: 'Interested', count: periodData.interested || 0, color: '#f59e0b' },
      { name: 'Lost', count: periodData.lost || 0, color: '#ef4444' }
    ];
  };

  const salesPerformanceData = getSalesPerformanceData();

  const getOverallSalesPerformanceData = () => {
    const periodData = stats?.salesPerformance?.[overallPeriod] || {};
    const categories = [
      { name: 'NEW', count: periodData.statusNew || 0, color: '#94a3b8' },
      { name: 'FOLLOW-UP', count: periodData.followUp || 0, color: '#eab308' },
      { name: 'HOT', count: periodData.hot || 0, color: '#f97316' },
      { name: 'COLD', count: periodData.cold || 0, color: '#38bdf8' },
      { name: 'CONVERTED', count: periodData.converted || 0, color: '#10b981' },
      { name: 'LOST', count: periodData.lost || 0, color: '#ef4444' },
      { name: 'MEETING', count: periodData.meeting || 0, color: '#a855f7' },
      { name: 'Total Calls', count: periodData.calls || 0, color: '#3b82f6' },
      { name: 'Ringing', count: periodData.ringing || 0, color: '#c084fc' },
      { name: 'Switch Off', count: periodData.switchOff || 0, color: '#ec4899' },
      { name: 'Not Pickup', count: periodData.notPickup || 0, color: '#6b7280' },
      { name: 'Not Interested', count: periodData.notInterested || 0, color: '#f43f5e' }
    ];
    return {
      totalLeads: periodData.totalLeads || 0,
      categories,
      pieData: categories
        .filter(c => c.name !== 'Total Calls')
        .map(c => ({ name: c.name, value: c.count, color: c.color }))
        .filter(p => p.value > 0)
    };
  };

  const overallSalesPerformanceData = getOverallSalesPerformanceData();

  const downloadCSV = () => {
    if (!overallSalesPerformanceData) return;
    
    // Prepare rows
    const rows = [
      ['Category', 'Count']
    ];
    
    // Add Total Leads row
    rows.push(['Total Leads', overallSalesPerformanceData.totalLeads]);
    
    // Add categories rows
    overallSalesPerformanceData.categories.forEach(cat => {
      rows.push([cat.name, cat.count]);
    });
    
    // Convert to CSV format
    const csvContent = "data:text/csv;charset=utf-8," 
      + rows.map(e => e.map(val => `"${val}"`).join(",")).join("\n");
      
    // Create download link and trigger click
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    
    const fileName = `overall_sales_performance_${overallPeriod}_${new Date().toISOString().split('T')[0]}.csv`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadPNG = () => {
    if (chartCardRef.current) {
      const downloadButtons = chartCardRef.current.querySelectorAll('.download-btn-class');
      downloadButtons.forEach(btn => btn.style.display = 'none');

      html2canvas(chartCardRef.current, { 
        useCORS: true,
        backgroundColor: '#ffffff',
        scale: 2
      }).then(canvas => {
        downloadButtons.forEach(btn => btn.style.display = 'flex');

        const link = document.createElement('a');
        link.download = `overall_sales_performance_${overallPeriod}_${new Date().toISOString().split('T')[0]}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }).catch(err => {
        downloadButtons.forEach(btn => btn.style.display = 'flex');
        console.error('Error generating image', err);
      });
    }
  };

  const leaveData = [
    { name: 'Approved', value: stats?.leaves?.approvedThisMonth || 0, color: '#10b981' },
    { name: 'Pending',  value: stats?.leaves?.pending           || 0, color: '#f59e0b' },
  ];

  const statCards = [
    { icon: 'fa-users',          count: stats?.employees?.total         ?? '—', label: 'Total Employees', color: 'blue' },
    { icon: 'fa-user-check',     count: stats?.attendance?.todayPresent ?? '—', label: 'Present Today',   color: 'green' },
    { icon: 'fa-user-times',     count: stats?.attendance?.todayAbsent  ?? '—', label: 'Absent Today',    color: 'red' },
    { icon: 'fa-calendar-minus', count: stats?.attendance?.todayOnLeave ?? '—', label: 'On Leave',        color: 'orange' },
    { icon: 'fa-tasks',          count: stats?.tasks?.pending           ?? '—', label: 'Pending Tasks',   color: 'blue' },
    { icon: 'fa-check-circle',   count: stats?.tasks?.completed         ?? '—', label: 'Tasks Done',      color: 'green' },
    { icon: 'fa-hourglass-half', count: stats?.leaves?.pending          ?? '—', label: 'Leave Requests',  color: 'orange' },
    { icon: 'fa-user-plus',      count: stats?.employees?.newThisMonth  ?? '—', label: 'New This Month',  color: 'green' },
  ];

  return (
    <div className="dashboard">
      {loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i>
          <p style={{ marginTop: 12 }}>Loading dashboard...</p>
        </div>
      )}

      {!loading && (
        <>
          <div className="stats-grid">
            {statCards.map((stat, i) => (
              <div key={i} className="stat-card">
                <div className={`stat-icon ${stat.color}`}>
                  <i className={`fas ${stat.icon}`}></i>
                </div>
                <div className="stat-info">
                  <h3>{stat.count}</h3>
                  <p>{stat.label}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="dashboard-charts">
            {/* Attendance Trend */}
            {attendanceData.length > 0 && (
              <div className="chart-card full-width">
                <h3><i className="fas fa-chart-line"></i> Attendance Trend (Last 7 Days)</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={attendanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                    <Legend />
                    <Line type="monotone" dataKey="present" stroke="#10b981" strokeWidth={2} name="Present" dot={{ fill: '#10b981', r: 4 }} />
                    <Line type="monotone" dataKey="absent"  stroke="#ef4444" strokeWidth={2} name="Absent"  dot={{ fill: '#ef4444', r: 4 }} />
                    <Line type="monotone" dataKey="leave"   stroke="#f59e0b" strokeWidth={2} name="On Leave" dot={{ fill: '#f59e0b', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Sales Team Performance */}
            {stats && (
              <div className="chart-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0 }}><i className="fas fa-chart-bar"></i> Sales Team Performance</h3>
                  <select 
                    value={salesPeriod} 
                    onChange={(e) => setSalesPeriod(e.target.value)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '13px',
                      background: '#fff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    <option value="today">Today</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={salesPerformanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                    <Bar dataKey="count" name="Leads" radius={[8, 8, 0, 0]}>
                      {salesPerformanceData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Overall Sales Performance */}
            {stats && (
              <div className="chart-card full-width" ref={chartCardRef}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                  <h3 style={{ margin: 0 }}><i className="fas fa-chart-pie"></i> Overall Sales Performance</h3>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {overallPeriod === 'custom' && (
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => {
                            setCustomStartDate(e.target.value);
                            handleCustomDateChange(e.target.value, customEndDate);
                          }}
                          style={{
                            padding: '5px 10px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '13px',
                            outline: 'none'
                          }}
                        />
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>to</span>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => {
                            setCustomEndDate(e.target.value);
                            handleCustomDateChange(customStartDate, e.target.value);
                          }}
                          style={{
                            padding: '5px 10px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            fontSize: '13px',
                            outline: 'none'
                          }}
                        />
                      </div>
                    )}
                    <select 
                      value={overallPeriod} 
                      onChange={(e) => setOverallPeriod(e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '13px',
                        background: '#fff',
                        cursor: 'pointer',
                        outline: 'none'
                      }}
                    >
                      <option value="today">Today</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="custom">Custom Range</option>
                    </select>
                    <button 
                      className="download-btn-class"
                      onClick={downloadCSV}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '13px',
                        background: '#f3f4f6',
                        color: '#374151',
                        cursor: 'pointer',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: 600,
                        transition: 'background 0.2s ease',
                      }}
                      onMouseOver={(e) => e.target.style.background = '#e5e7eb'}
                      onMouseOut={(e) => e.target.style.background = '#f3f4f6'}
                    >
                      <i className="fas fa-file-csv"></i> Download CSV
                    </button>
                    <button 
                      className="download-btn-class"
                      onClick={downloadPNG}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '13px',
                        background: '#3b82f6',
                        color: '#fff',
                        cursor: 'pointer',
                        outline: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontWeight: 600,
                        transition: 'background 0.2s ease',
                      }}
                      onMouseOver={(e) => e.target.style.background = '#2563eb'}
                      onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                    >
                      <i className="fas fa-download"></i> Download Image
                    </button>
                  </div>
                </div>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '30px', alignItems: 'center', justifyContent: 'center', minHeight: '320px' }}>
                  {/* Left part: Donut chart with Total Leads in the center */}
                  <div style={{ position: 'relative', width: '280px', height: '280px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                        <Pie
                          data={overallSalesPerformanceData.pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={75}
                          outerRadius={105}
                          paddingAngle={3}
                          dataKey="value"
                        >
                          {overallSalesPerformanceData.pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    
                    {/* Centered Leads label */}
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      pointerEvents: 'none'
                    }}>
                      <span style={{ fontSize: '32px', fontWeight: 800, color: '#111827', display: 'block', lineHeight: 1 }}>
                        {overallSalesPerformanceData.totalLeads}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Total Leads
                      </span>
                    </div>
                  </div>
                  
                  {/* Right part: Custom grid legend listing each category & counts */}
                  <div style={{
                    flex: '1',
                    minWidth: '280px',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '12px',
                    background: '#f8fafc',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #f1f5f9'
                  }}>
                    {/* Custom Header displaying Total Leads in the legend */}
                    <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0 8px 0', borderBottom: '2px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: '#6366f1' }}></span>
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>Total Leads</span>
                      </div>
                      <strong style={{ fontSize: '16px', color: '#1e293b' }}>{overallSalesPerformanceData.totalLeads}</strong>
                    </div>
                    {overallSalesPerformanceData.categories.map((cat, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: cat.color }}></span>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: '#475569' }}>{cat.name}</span>
                        </div>
                        <strong style={{ fontSize: '14px', color: '#0f172a' }}>{cat.count}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Leave Status Pie */}
            {leaveData.some(l => l.value > 0) && (
              <div className="chart-card">
                <h3><i className="fas fa-chart-pie"></i> Leave Status</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={leaveData} cx="50%" cy="50%" outerRadius={90}
                      labelLine={false}
                      label={({ name, percent }) => percent > 0 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                      dataKey="value">
                      {leaveData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="legend-list">
                  {leaveData.map((item, i) => (
                    <div key={i} className="legend-item">
                      <span className="legend-color" style={{ backgroundColor: item.color }}></span>
                      <span>{item.name}: {item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════
   ROOT DASHBOARD — role-based switcher
══════════════════════════════════════════════ */
const Dashboard = () => {
  const { currentUser, isPrivileged } = useAuth();

  if (isPrivileged) return <AdminDashboard />;
  return <EmployeeDashboard currentUser={currentUser} />;
};

export default Dashboard;


